import { useEffect, useRef, useState } from 'react';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import {
  MAX_PLAYER_NAME_CHARS,
  validatePlayerName,
} from '@spirits/shared';
import { functions } from '../firebase';

const setPlayerNameCallable = httpsCallable<{ name: string }, { ok: boolean; playerName: string }>(
  functions,
  'setPlayerName',
);

interface Props {
  playerName: string;
}

export function PlayerNamePill({ playerName }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="rename yourself"
        className="text-xs text-spirit-fog/70 hover:text-spirit-gold transition-colors px-2 py-1 rounded-md border border-spirit-fog/20 hover:border-spirit-gold/40"
      >
        you · <span className="text-spirit-fog">{playerName}</span>
      </button>
      {open && (
        <RenameModal currentName={playerName} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

interface ModalProps {
  currentName: string;
  onClose: () => void;
}

function RenameModal({ currentName, onClose }: ModalProps) {
  const [draft, setDraft] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  async function save() {
    const v = validatePlayerName(draft);
    if (!v.ok) {
      setError(v.error);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await setPlayerNameCallable({ name: v.normalized });
      onClose();
    } catch (e) {
      const fe = e as FunctionsError;
      setError(fe?.message ?? 'something refused that name');
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-spirit-night/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl bg-spirit-night border border-spirit-fog/20 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl text-spirit-fog mb-2">Choose a name</h2>
        <p className="text-sm text-spirit-fog/60 mb-4">
          The name bots credit when you help them.
        </p>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          maxLength={MAX_PLAYER_NAME_CHARS}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') onClose();
          }}
          disabled={saving}
          className="w-full rounded-md bg-spirit-night/60 border border-spirit-fog/30 px-3 py-2 text-spirit-fog text-lg focus:outline-none focus:border-spirit-gold/70"
        />
        <div className="mt-1 flex justify-between text-xs text-spirit-fog/40">
          <span>{error ? <span className="text-spirit-rose">{error}</span> : 'letters, spaces, hyphens, apostrophes'}</span>
          <span>{draft.length}/{MAX_PLAYER_NAME_CHARS}</span>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-sm text-spirit-fog/60 hover:text-spirit-fog"
          >
            cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || draft.trim() === currentName.trim()}
            className="px-4 py-1.5 rounded-md text-sm bg-spirit-gold/80 hover:bg-spirit-gold text-spirit-night font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'saving…' : 'name me'}
          </button>
        </div>
      </div>
    </div>
  );
}
